import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  reopenAccessCode,
  startSelfServe,
  validateAccessCode,
  type ValidateReason,
} from "../lib/api";
import { INTAKE_FIELDS, NO_ATTACH_NOTE } from "../lib/intake";
import { CLOUD_ENGINES } from "../lib/engines.generated";
import { DEFAULT_SERVER, SERVER_STORAGE_KEY } from "../lib/server-config";
import { normalizeAccessCode } from "../lib/access-code";
import { CloudMast } from "./CloudMast";
import { SaveCodeModal } from "./SaveCodeModal";
import { useReveal } from "../lib/useReveal";

const SAMPLE_VIEWER_URL = "https://plaintosee.cc/sample";
// Same-origin /apply — handled by Shell's path check. Keeping it as a
// plain anchor (not router push) preserves middle-click-to-new-tab.
// Only linked from the CLOSED (pre-flip) posture — see selfServeOpen below;
// per the front-door spec, /apply is unlinked (not removed) once the
// self-serve switch is ON.
const APPLY_URL = "/apply";
const INTAKE_MAILTO =
  "mailto:hello@plaintosee.cc" +
  "?subject=" + encodeURIComponent("Aurora intake — tell us what you have") +
  "&body=" + encodeURIComponent(
    NO_ATTACH_NOTE + "\n\n" +
    "A few details (optional) so we can figure out how to help:\n\n" +
    INTAKE_FIELDS
  );

// Where to validate the entered code. Mirrors OperatorShell's same-
// origin-in-dev / production-host fallback.
function resolveServer(): string {
  // In dev (localhost), prefer same-origin so the Vite proxy can
  // forward backend route prefixes. A stale localStorage value pointing
  // at the prior :8787 direct address would bypass the proxy and rely
  // on CORS — usually works, but a previous test session may have
  // stored a URL that no longer resolves. Force same-origin in dev.
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return window.location.origin;
    }
  }
  try {
    const stored = localStorage.getItem(SERVER_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // localStorage unavailable
  }
  return DEFAULT_SERVER;
}

// A delivered result the reopen check found still inside its retention
// window — enough to build the same fragment the ready email links to
// (see openReopenedRun below). daysLeft is display-only.
type ReopenInfo = {
  cloudRunId: string;
  runToken: string;
  daysLeft: number | null;
};

// "available for {days_left} more days", with the two edges named
// explicitly: 0 (janitor sweeps ~6-hourly, so "0 days left" can still mean
// hours) reads as "less than a day" rather than the nonsensical "0 more
// days"; 1 is singularized. days_left is occasionally null (exited_at
// missing/unparseable server-side) — reads as a plain "ready to reopen"
// with no day count rather than showing "null".
function reopenAvailabilityCopy(daysLeft: number | null): string {
  if (daysLeft === null) {
    return "Your analysis is ready — it's available to reopen.";
  }
  if (daysLeft <= 0) {
    return "Your analysis is ready — it's available for less than a day.";
  }
  if (daysLeft === 1) {
    return "Your analysis is ready — it's available for 1 more day.";
  }
  return `Your analysis is ready — it's available for ${daysLeft} more days.`;
}

// selfServeOpen distinguishes the "used" ending: apply-era codes come with
// an invite email to reply to, but a self-serve mint never had one — "reply
// to your invite" is a dead end for those users (2026-07-04 UX review).
function errorForReason(
  reason: ValidateReason | null,
  selfServeOpen: boolean,
): string {
  switch (reason) {
    case "used":
      return (
        "This code already started its analysis. When it finishes, the " +
        "link in your “analysis ready” email opens the result — check " +
        "the inbox you gave us. " +
        (selfServeOpen
          ? "Need to run another record? Start now above mints a fresh code."
          : "Need a fresh code? Reply to your invite.")
      );
    case "no_fresh_starts":
      return "This code has used both of its starts — your first, plus one more after stopping early. Reply to the email that brought your code and we'll set you up to run again.";
    case "revoked":
      return "Code revoked. Reply to your invite if you think that's a mistake.";
    case "expired":
      return "Code expired. Reply to your invite for a fresh one.";
    case "unknown":
    default:
      return "Code not recognized. If you were sent a code recently, double-check the spelling.";
  }
}

// --- Turnstile widget (mirrors ApplyForm's wiring; kept local rather than
// shared so neither file's global augmentation risks colliding with the
// other's — the shapes are identical by construction). Unset site key
// (local dev, tests) means the widget never mounts and the token stays
// null — the mint gate below only blocks when a site key IS configured. ---

function readTurnstileSiteKey(): string {
  return (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "").trim();
}
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";

interface TurnstileWidgetApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
      theme?: "auto" | "light" | "dark";
    },
  ) => string;
  remove: (widgetId: string) => void;
}

function getTurnstileGlobal(): TurnstileWidgetApi | undefined {
  return (window as unknown as { turnstile?: TurnstileWidgetApi }).turnstile;
}

type Props = {
  onAccept: (code: string) => void;
  // Dev-only convenience prefill (Shell passes the configured dev code in
  // dev, "" in prod). Just seeds the input; the user can edit it and it
  // still goes through normal validation.
  initialCode?: string;
  // Door state (self_serve_open), resolved by Shell's single /config fetch
  // on mount and handed down as a prop. Landing used to fetch this itself
  // and relay it back up via onSelfServeOpenChange — but an entry path that
  // skips Landing entirely (a returning visitor restored straight into
  // Welcome, code already stored) never gave it the chance, so Welcome's
  // "Early access" pill kept showing even once the door was open (live on
  // staging, 2026-07-04). Shell now owns the one fetch; this is just the
  // resolved answer. Defaults to the same CLOSED (early-access) fallback
  // Shell itself uses before its fetch resolves / if it fails.
  selfServeOpen?: boolean;
  // True when Landing is rendering because the user explicitly backed out
  // of the Welcome/gate (mast-logo click or browser Back) rather than
  // arriving fresh — their code/session is still saved, just not on screen.
  // Surfaces the "continue where you left off" affordance so they don't
  // have to retype a code (2026-07-04 escape-hatch fix).
  resumeAvailable?: boolean;
  // Returns to the gate exactly where they left it. Shell just flips back
  // to rendering Welcome — accessCode/welcomed were never touched, so
  // nothing here needs to pass any state back.
  onResume?: () => void;
};

export function Landing({
  onAccept,
  initialCode = "",
  selfServeOpen = false,
  resumeAvailable = false,
  onResume,
}: Props) {
  const [code, setCode] = useState(initialCode);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  // Set only after a "used" verdict's follow-up /access-codes/reopen call
  // confirms the delivered result is still available (2026-07-04). Replaces
  // the static "used" error text with a reopen affordance. Cleared on any
  // new submit/edit so a stale answer can't survive onto a different code.
  const [reopenInfo, setReopenInfo] = useState<ReopenInfo | null>(null);
  // Guards the fire-and-forget reopen check against a race: if the user
  // edits/resubmits before it resolves, a stale "available" answer for the
  // PREVIOUS code must not land on the door (mirrors the cancelled-flag
  // pattern used by this file's effects, adapted for an event handler).
  const reopenRequestIdRef = useRef(0);

  // When a saved session exists (resumeAvailable), the door panel defaults
  // to the welcome-back card instead of the normal fresh door — see the
  // render logic below. "Start fresh instead" flips this to reveal the
  // ordinary door underneath; it's a pure display toggle (no navigation, no
  // storage write) so the saved session is untouched either way (2026-07-04
  // door-swap fix — replaces the old stacked .landing-resume-cta strip,
  // which left the fresh door's Start-now button as the dominant, and
  // wrong, path for a returning visitor).
  const [showFreshDoor, setShowFreshDoor] = useState(false);

  const [minting, setMinting] = useState(false);
  const [mintError, setMintError] = useState<string | null>(null);
  // Truthy mintedCode now gates the SaveCodeModal overlay (front-door
  // redesign, 2026-07-04) rather than swapping the door panel in place —
  // see SaveCodeModal.tsx. The door underneath keeps rendering its normal
  // pre-mint content; "Not now" resets this to null and the overlay lifts.
  const [mintedCode, setMintedCode] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const turnstileSiteKey = readTurnstileSiteKey();

  // Scroll-in reveal (2026-07-04, ported from the marketing site — see
  // src/lib/useReveal.ts) for below-fold content blocks only. The hero and
  // the door/Get-started panel deliberately do NOT get a ref here: people
  // act there, and that action must never wait on a scroll animation.
  const howRef = useReveal<HTMLElement>();
  const sampleRef = useReveal<HTMLElement>();
  const sourcesRef = useReveal<HTMLElement>();
  const principlesRef = useReveal<HTMLElement>();
  const questionsRef = useReveal<HTMLParagraphElement>();

  // Mount the Turnstile widget only once the door is confirmed open and
  // before a code has been minted (nothing left to verify after that).
  // showFreshDoor is in the deps for a real reason, not tidiness: the
  // Turnstile container only exists in the DOM once the fresh door
  // renders — the welcome-back card branch has no container at all. A
  // returning visitor who clicks "Start fresh instead" mounts the
  // container for the FIRST time well after this effect's initial run (at
  // which point selfServeOpen/mintedCode/turnstileSiteKey were already
  // settled and didn't change again), so without showFreshDoor here the
  // effect never re-fires, the container ref stays unattached forever, and
  // the widget silently never renders — turnstileToken stays null and
  // handleStartNow's gate blocks with "Please complete the security
  // check." forever (2026-07-04, Finding 1). The cleanup below already
  // tears down any previously-rendered widget before this body re-runs, so
  // re-firing here can't double-mount.
  useEffect(() => {
    if (!selfServeOpen || mintedCode || !turnstileSiteKey) return;
    let cancelled = false;

    function renderWidget() {
      if (cancelled) return;
      const ts = getTurnstileGlobal();
      if (!turnstileContainerRef.current || !ts) return;
      setTurnstileToken(null);
      turnstileWidgetIdRef.current = ts.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token: string) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(null),
        "error-callback": () => setTurnstileToken(null),
        theme: "light",
      });
    }

    if (getTurnstileGlobal()) {
      renderWidget();
    } else {
      let script = document.getElementById(
        TURNSTILE_SCRIPT_ID,
      ) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      const poll = window.setInterval(() => {
        if (getTurnstileGlobal()) {
          window.clearInterval(poll);
          renderWidget();
        }
      }, 100);
      window.setTimeout(() => window.clearInterval(poll), 10_000);
    }

    return () => {
      cancelled = true;
      const ts = getTurnstileGlobal();
      if (turnstileWidgetIdRef.current && ts) {
        try {
          ts.remove(turnstileWidgetIdRef.current);
        } catch {
          // already removed
        }
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [selfServeOpen, mintedCode, turnstileSiteKey, showFreshDoor]);

  // A "used" code might still be inside its 7-day retention window — check
  // whether the delivered result can be reopened from here (2026-07-04),
  // instead of leaving the user to dig up the ready email. Fire-and-forget:
  // never blocks the door, and any failure (network, 429, unavailable)
  // silently leaves the "used" message the caller already set standing.
  function checkReopen(normalizedCode: string, requestId: number) {
    reopenAccessCode(resolveServer(), normalizedCode)
      .then((res) => {
        // A newer submit superseded this one — drop a stale answer rather
        // than have it land on whatever code is in the field now.
        if (reopenRequestIdRef.current !== requestId) return;
        if (res.available && res.cloud_run_id && res.run_token) {
          setReopenInfo({
            cloudRunId: res.cloud_run_id,
            runToken: res.run_token,
            daysLeft: res.days_left,
          });
        }
      })
      .catch(() => {
        // Today's "used" message already stands — nothing more to do.
      });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const normalized = normalizeAccessCode(code);
    if (!normalized) {
      setError("Enter the code you were sent.");
      return;
    }
    setError(null);
    setReopenInfo(null);
    const requestId = ++reopenRequestIdRef.current;
    setVerifying(true);
    try {
      const res = await validateAccessCode(resolveServer(), normalized);
      if (res.valid) {
        onAccept(normalized);
        return;
      }
      setVerifying(false);
      setError(errorForReason(res.reason, selfServeOpen));
      if (res.reason === "used") {
        checkReopen(normalized, requestId);
      }
    } catch (err) {
      setVerifying(false);
      // 429 deserves its own message — a user who typo'd a confusable
      // suffix a few times and triggered the rate limit otherwise sees
      // the generic network error and assumes the site is broken.
      const apiStatus = (err as { status?: number })?.status;
      if (apiStatus === 429) {
        setError(
          "Too many attempts. Please wait a minute and try again.",
        );
      } else {
        setError("Could not check code right now. Try again in a moment.");
      }
    }
  }

  // Navigate into the SAME deep-link path the ready email uses (#run=…&
  // token=…) — Shell/App both read the fragment once via a useState
  // initializer at mount, so only a fresh boot makes either of them see it.
  // Assigning window.location.href to a URL that differs ONLY in the
  // fragment does NOT trigger a real navigation/reload — same-document
  // fragment navigations just fire hashchange, which nothing here listens
  // for (the earlier version of this fix assumed href assignment alone was
  // enough; it wasn't — confirmed dead on live staging with a real click).
  // Set the fragment, then force the reboot with an explicit reload() so
  // the existing deep-link parser picks it up on the fresh mount — this
  // mirrors arriving via the "ready" email link, which is also a fresh
  // load. Fine to run through the escape-hatch history model added in
  // 2026-07-04-shell-config.md: that only pushes a history entry while
  // sitting at the gate (Welcome), which this door never is, and
  // location.hash + reload() never fires popstate either way.
  function openReopenedRun() {
    if (!reopenInfo) return;
    const fragment = new URLSearchParams({
      run: reopenInfo.cloudRunId,
      token: reopenInfo.runToken,
    }).toString();
    window.location.hash = fragment;
    window.location.reload();
  }

  async function handleStartNow() {
    if (turnstileSiteKey && !turnstileToken) {
      setMintError("Please complete the security check.");
      return;
    }
    setMintError(null);
    setMinting(true);
    try {
      const res = await startSelfServe(resolveServer(), turnstileToken);
      setMintedCode(res.code);
    } catch (err) {
      const apiStatus = (err as { status?: number })?.status;
      if (apiStatus === 429) {
        setMintError("Too many attempts. Please wait a bit and try again.");
      } else if (apiStatus === 403) {
        setMintError("Security check failed. Please refresh the page and try again.");
      } else if (apiStatus === 404) {
        setMintError("Starting isn't available right now — please try again shortly.");
      } else {
        setMintError("Could not start right now. Try again in a moment.");
      }
    } finally {
      setMinting(false);
    }
  }

  // Shared between the OPEN posture's secondary door and the CLOSED
  // posture's only door — same state, same handler, different label.
  function codeEntryForm(labelText: string): ReactNode {
    return (
      <form className="landing-code-form" onSubmit={handleSubmit} noValidate>
        <label className="landing-code-label" htmlFor="landing-code">
          {labelText}
        </label>
        <input
          id="landing-code"
          className="landing-code-input"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="XXXX-XXXX-XXXX-XXXX"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            if (error) setError(null);
            if (reopenInfo) setReopenInfo(null);
            // Invalidate any in-flight reopen lookup for the code being
            // edited away from — otherwise a stale "available" answer for
            // the code that was JUST replaced can still land here after the
            // edit, repainting the reopen affordance for the wrong code
            // (2026-07-04, Finding 2). handleSubmit bumps this same ref for
            // a fresh submit; bumping on every edit closes the gap between
            // "submitted" and "edited before the check resolved."
            reopenRequestIdRef.current += 1;
          }}
          disabled={verifying}
        />
        {reopenInfo ? null : (
          // Hidden (not just disabled) while the reopen affordance is
          // showing for this code — otherwise two buttons sit stacked for
          // one code (Continue above, Reopen my result below), ambiguous
          // about which to press. Reappears the moment the affordance
          // clears, including on edit (onChange above already resets
          // reopenInfo to null for any edit to the code field).
          <button
            type="submit"
            className="landing-code-submit"
            disabled={verifying}
          >
            {verifying ? "Verifying…" : "Continue"}
          </button>
        )}
        {reopenInfo ? (
          <div className="landing-reopen">
            <p className="landing-reopen-msg">
              {reopenAvailabilityCopy(reopenInfo.daysLeft)}
            </p>
            <button
              type="button"
              className="landing-start-now-btn"
              onClick={openReopenedRun}
            >
              Reopen my result →
            </button>
          </div>
        ) : error ? (
          <div className="landing-code-error">{error}</div>
        ) : null}
      </form>
    );
  }

  return (
    <main className="landing">
     {mintedCode ? (
       <SaveCodeModal
         code={mintedCode}
         onContinue={() => onAccept(mintedCode)}
         onNotNow={() => setMintedCode(null)}
       />
     ) : null}
     <div className="landing-wrap">
      <CloudMast
        nav={<a className="landing-mast-link" href="/faq">FAQ</a>}
        hideStatus={selfServeOpen}
      />

      {selfServeOpen ? null : (
        <div className="landing-pilot-banner">
          <span className="landing-pilot-dot" aria-hidden="true"></span>
          <strong>Aurora is in early access.</strong> We set up each person directly — sponsored access for those who qualify, a flat fee per analysis otherwise.
        </div>
      )}

      <section className="landing-hero">
        <div className="landing-hero-head">
          <div className="landing-eyebrow">A court-preparation tool</div>
          <h1 className="landing-title">
            Coercive control hides in plain sight.<br />
            <em>Let's read your record.</em>
          </h1>
        </div>

        <div className="landing-hero-cols">
        <div className="landing-hero-text">
          <p className="landing-deck">
            <span><strong>Coercive control</strong> hides in routine communication.</span>
            <span>Aurora reads for the pattern.</span>
          </p>
          <p className="landing-lede">
            Aurora reads every message in your co-parenting platform
            export and returns a cited brief mapping the recurring
            patterns it finds — schedule control, contradictory accounts,
            isolation, demeaning, financial pressure. What turns a record
            into one of coercive control rather than ordinary conflict is
            the constellation, and that's hard to see by hand.
          </p>
        </div>

        <div className="landing-hero-side">
          {resumeAvailable && !showFreshDoor ? (
            // Welcome-back card REPLACES the door panel entirely — no
            // Start-now button, no code box, nothing that could orphan the
            // saved session by mistake. The only two exits are Continue
            // (the actual saved session) and "Start fresh instead" (an
            // explicit, deliberate choice to reveal the ordinary door).
            <div className="landing-welcome-back">
              <div className="landing-code-label">Welcome back</div>
              <p className="landing-doors-sub">
                You're set up — your code and your place are saved in this
                browser.
              </p>
              <button
                type="button"
                className="landing-start-now-btn"
                onClick={onResume}
              >
                Continue where you left off →
              </button>
              <p className="landing-start-fresh-row">
                <button
                  type="button"
                  className="landing-link landing-link-btn"
                  onClick={() => setShowFreshDoor(true)}
                >
                  Start fresh instead
                </button>
              </p>
            </div>
          ) : (
            <>
              {resumeAvailable ? (
                // Only reachable via "Start fresh instead" above — a plain
                // fresh visitor (resumeAvailable false) never sees this.
                // True as of 2026-07-04: neither viewing this door nor
                // minting-and-not-yet-confirming a code touches the saved
                // session; it's overwritten only once a new code is
                // actually accepted (Start now → Continue, or a different
                // code submitted here).
                <p className="landing-fresh-notice">
                  Your saved session stays until you start a new one.
                </p>
              ) : null}
              {selfServeOpen ? (
                <>
                  <h2 className="landing-doors-h">Get started</h2>
                  <p className="landing-doors-sub">
                    Start your analysis — no account, no application.
                  </p>
                  <p className="landing-doors-price">
                    <b>From $199 a case</b> · pay what fits.
                  </p>
                  <button
                    type="button"
                    className="landing-start-now-btn"
                    onClick={handleStartNow}
                    disabled={minting}
                  >
                    {minting ? "Starting…" : "Start now →"}
                  </button>
                  {turnstileSiteKey ? (
                    <div className="landing-turnstile" ref={turnstileContainerRef} />
                  ) : null}
                  {mintError ? (
                    <div className="landing-code-error">{mintError}</div>
                  ) : null}

                  <div className="landing-doors-divider"><span>or</span></div>

                  {codeEntryForm("Got a code?")}
                </>
              ) : (
                <>
                  <h2 className="landing-doors-h">Get started</h2>
                  <p className="landing-doors-sub">
                    Already set up? Enter your code. New here? Request access.
                  </p>
                  {codeEntryForm("Have a code?")}

                  <div className="landing-doors-divider"><span>or</span></div>

                  <div className="landing-door-new">
                    <div className="landing-code-label">New here?</div>
                    <a className="landing-request-btn" href={APPLY_URL}>
                      Request early access →
                    </a>
                    <p className="landing-door-micro">
                      A few questions about your case and we'll set you up. Sponsored
                      for qualifying pro&nbsp;se · a flat per-analysis fee otherwise.
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        </div>
      </section>

      <section className="landing-how" ref={howRef}>
        <h2 className="landing-h2">How it works</h2>
        <div className="landing-how-grid">
          <div className="landing-how-step">
            <div className="landing-how-num">1</div>
            <h3>You upload your record</h3>
            <p>
              You export your messages from your co-parenting platform —
              OurFamilyWizard, for example — and drop the PDF in.
              Nothing to install.
            </p>
          </div>
          <div className="landing-how-step">
            <div className="landing-how-num">2</div>
            <h3>Aurora reads it</h3>
            <p>
              It reads every message for the patterns the literature names,
              usually in 15–30 minutes. Your messages are used only to
              run the analysis, then let go — never shared, never used to
              train it.
            </p>
          </div>
          <div className="landing-how-step">
            <div className="landing-how-num">3</div>
            <h3>You download the brief</h3>
            <p>
              You get back one file that names each pattern and links it to
              the exact messages behind it. Open it with a passphrase and
              take it to your attorney or advocate — it stays on your device
              unless you send it.
            </p>
          </div>
        </div>
      </section>

      <section className="landing-sample" ref={sampleRef}>
        <div className="landing-sample-text">
          <h2 className="landing-h2">See what you'll get.</h2>
          <p className="landing-sample-lede">
            A full analysis on a fictional case — names and messages
            invented for demonstration. It shows the shape of the output:
            every claim footnoted to a specific message, every pattern
            grounded in the published literature.
          </p>
        </div>
        <figure className="landing-sample-figure">
          <img
            className="landing-preview-img"
            src="/screens/viewer-preview.png"
            width={1800}
            height={1087}
            loading="lazy"
            decoding="async"
            alt="Sample Aurora analysis: a list of patterns the tool detected, an open pattern showing cited claims, and the source messages those claims came from."
          />
        </figure>
        <a className="landing-cta" href={SAMPLE_VIEWER_URL} target="_blank" rel="noopener">
          View the sample analysis →
        </a>
      </section>

      <section className="landing-sources" ref={sourcesRef}>
        <div className="landing-sources-col">
          <h3>Sources supported today</h3>
          <ul className="landing-sources-list">
            {/* Fully derived from the engine catalog (like Welcome's cards) —
                this list went stale by hand once (2houses missing at
                go-live), and a hardcoded "(PDF export)" suffix would mislabel
                the first non-PDF engine the same way (review, 2026-07-10). */}
            {CLOUD_ENGINES.map((e) => (
              <li key={e.id}>
                {e.display} <span>({e.exportLabel})</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="landing-sources-col">
          <h3>Have a different export?</h3>
          <p>
            If your messages live somewhere else — another co-parenting
            app, email, a set of screenshots — tell us what you have and
            we'll see if we can help.
          </p>
          <a className="landing-cta-link" href={INTAKE_MAILTO}>
            Tell us what you have →
          </a>
        </div>
      </section>

      <section className="landing-principles" ref={principlesRef}>
        <div className="landing-principle">
          <h3>What Aurora is not</h3>
          <p>
            Aurora does not give legal advice and does not diagnose
            people. It is a forensic pattern-analysis tool. It surfaces
            what is already in the record so the people who can act on
            it — your attorney, your advocate, the court — can read it
            in less time.
          </p>
        </div>
        <div className="landing-principle">
          <h3>Methodology</h3>
          <p>
            The pattern catalog is grounded in the established academic
            literature on coercive control — not invented for the
            product. It's versioned and cited in every brief, so a
            reader can check the source.
          </p>
          <div className="landing-frameworks">
            Grounded in <span>Stark</span> · <span>Fontes</span> · <span>Katz</span>
          </div>
        </div>
      </section>

      <p className="landing-questions" ref={questionsRef}>
        More questions? See the{" "}
        <a className="landing-link" href="/faq">FAQ</a>, or email{" "}
        <a className="landing-link" href="mailto:hello@plaintosee.cc">
          hello@plaintosee.cc
        </a>
        .
      </p>

      <footer className="landing-foot">
        <div className="landing-foot-left">
          Aurora · Built independently ·{" "}
          <a href="https://plaintosee.cc">plaintosee.cc ↗</a>
          <a
            className="landing-jta-badge"
            href="https://justicetechassociation.org/member-directory/aurora-53551491"
            target="_blank"
            rel="noopener"
            aria-label="Member · Justice Technology Association"
          >
            <img src="/jta-member-dark.svg" alt="Member · Justice Technology Association" />
          </a>
        </div>
        <div className="landing-foot-right">&copy; 2026 Aurora</div>
      </footer>
     </div>
    </main>
  );
}
